import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import {
  getLicenseRequests,
  getRequestsForLicense as getForLicense,
  acknowledgeRequests as acknowledge,
  getLicenseAccessRequests as getAccessRequests,
  getAddSeatRequests as getLicenseSeatRequests,
  type LicenseRequestType,
  type LicenseRequestInfo
} from '@/helpers/apiDashboardWrappers';
import { resolveApiPromise, type ApiError } from '@/helpers/apiHelpers';

export const useLicenseRequestStore = defineStore('licenseRequests', () => {
  const requestsLocal = ref({
    inProgress: false,
    result: undefined as Map<string, LicenseRequestInfo> | undefined,
    error: undefined as ApiError | undefined
  });
  const requests = computed(() => requestsLocal.value);

  watch(
    requests,
    ({ inProgress: isLoading, result: data }) => {
      if (isLoading || data == null) {
        return '';
      }

      allRequestsCount.value = Array.from(data.entries())
        .reduce((acc, [_, info]) => {
          acc += info.addSeatRequestCount + info.groupAccessRequestCount + info.renewRequestCount + info.tradeUpRequestCount;
          return acc;
        }, 0);
    }, { deep: true });

  const allRequestsCount = ref(undefined as number | undefined);
  const requestsCount = computed(() => allRequestsCount.value);

  async function getRequests() {
    const requests = await getLicenseRequests();

    return requests.reduce((acc, cur) => {
      acc.set(cur.licenseId ?? '', cur);

      return acc;
    }, new Map<string, LicenseRequestInfo>());
  }

  function loadRequests() {
    requestsLocal.value = ref(resolveApiPromise(getRequests())).value;
  }

  async function getRequestsForLicense(licenseId: string, type: LicenseRequestType) {
    try {
      return await getForLicense(licenseId, type);
    }
    catch {
      return undefined;
    }
  }

  async function getLicenseAccessRequests() {
    try {
      return await getAccessRequests();
    }
    catch {
      return undefined;
    }
  }

  async function acknowledgeRequests(ids: string[]) {
    return await acknowledge(ids);
  }

  async function getAddSeatRequests() {
    try {
      return await getLicenseSeatRequests();
    }
    catch {
      return undefined;
    }
  }

  return {
    requests,
    requestsCount,
    loadRequests,
    getRequestsForLicense,
    acknowledgeRequests,
    getLicenseAccessRequests,
    getAddSeatRequests
  };
});